/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import {
  gitHubPullRequestComparableVersionsAtom,
  gitHubPullRequestVersionsAtom,
  gitHubPullRequestViewerLastReviewCommitAtom,
} from './jotai';
import {Button} from '@primer/react';
import {useAtomValue, useSetAtom} from 'jotai';

/**
 * Button that switches the diff view to show only changes since the viewer's
 * last review. Shows the version diff from the review commit to the latest HEAD,
 * with inline comments visible in context.
 *
 * Only renders when the viewer has previously reviewed the PR and there are
 * newer commits since that review.
 */
export default function ChangesSinceLastReviewButton(): React.ReactElement | null {
  const lastReviewCommit = useAtomValue(gitHubPullRequestViewerLastReviewCommitAtom);
  const versions = useAtomValue(gitHubPullRequestVersionsAtom);
  const comparableVersions = useAtomValue(gitHubPullRequestComparableVersionsAtom);
  const setComparableVersions = useSetAtom(gitHubPullRequestComparableVersionsAtom);

  if (lastReviewCommit == null || versions.length === 0) {
    return null;
  }

  const latestVersion = versions[versions.length - 1];
  const latestCommit = latestVersion.headCommit;

  // Don't show if the last review was on the latest commit (nothing new to see)
  if (lastReviewCommit === latestCommit) {
    return null;
  }

  // Don't show if already viewing changes since last review
  if (
    comparableVersions != null &&
    comparableVersions.beforeCommitID === lastReviewCommit &&
    comparableVersions.afterCommitID === latestCommit
  ) {
    return null;
  }

  return (
    <Button
      size="small"
      onClick={() => {
        setComparableVersions({
          beforeCommitID: lastReviewCommit,
          afterCommitID: latestCommit,
        });
      }}>
      Changes since last review
    </Button>
  );
}
